import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Leaf, Clock, ArrowLeft, ArrowRight, Tag, IndianRupee, Award, Zap, Shield } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { blogPosts, getPostBySlug } from "@/data/blogPosts";

const HERO_ICONS = { IndianRupee, Award, Zap, Shield };

const F = {
  950: "#2d2219", 900: "#3a2e28", 800: "#504136", 700: "#5e4e41",
  500: "#689689", 400: "#83E8BA", 300: "#8ab0a8",
  200: "#B2E6D4", 100: "#c8ede4", 50: "#edf8f4",
  e500: "#A49E8D", e300: "#c0b9ac", e100: "#f0ede8",
  m600: "#2d4f4b", m500: "#3d6b65", m400: "#4a7068", m300: "#5e8480",
  c100: "#f5f2ec", c200: "#e8e3d8", c50: "#faf9f6",
};

// ── Hero banner ───────────────────────────────────────────────────────────────
function HeroBanner({ hero }) {
  const Icon = HERO_ICONS[hero.icon] || Zap;
  const [from, to] = hero.gradient;
  return (
    <div className="rounded-2xl overflow-hidden mb-8" style={{ border: `1px solid rgba(255,255,255,0.06)` }}>
      {/* Photo */}
      {hero.image && (
        <div className="relative overflow-hidden" style={{ height: 220 }}>
          <img src={hero.image} alt="" className="w-full h-full object-cover" />
          {/* subtle gradient overlay at bottom for chip readability */}
          <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 55%)" }} />
          {/* Chips over photo */}
          <div className="absolute bottom-4 left-4 flex flex-wrap gap-2">
            {hero.chips.map((chip) => (
              <span key={chip} className="text-xs px-2.5 py-1 rounded-full font-medium backdrop-blur-sm"
                style={{ background: "rgba(0,0,0,0.45)", color: "#fff", border: "1px solid rgba(255,255,255,0.2)" }}>
                {chip}
              </span>
            ))}
          </div>
          {/* Stat badge top-right */}
          <div className="absolute top-4 right-4 text-right px-3 py-2 rounded-xl backdrop-blur-sm"
            style={{ background: "rgba(0,0,0,0.5)", border: "1px solid rgba(255,255,255,0.15)" }}>
            <p className="text-xl font-black" style={{ color: F[400], fontFamily: "Chivo, sans-serif" }}>{hero.stat}</p>
            <p className="text-xs" style={{ color: "rgba(255,255,255,0.7)" }}>{hero.statLabel}</p>
          </div>
        </div>
      )}
      {/* Charger image strip (vendor article only) */}
      {hero.images && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px" style={{ background: `linear-gradient(135deg, ${from} 0%, ${to} 100%)`, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          {hero.images.map((img) => (
            <div key={img.src} className="relative overflow-hidden" style={{ height: 100, background: "#111" }}>
              <img src={img.src} alt={img.label}
                className="w-full h-full object-contain p-3 transition-transform duration-300 hover:scale-105"
                style={{ background: "#1a1a1a" }} />
              <div className="absolute bottom-0 inset-x-0 px-2 py-1" style={{ background: "rgba(0,0,0,0.65)" }}>
                <p className="text-xs font-semibold truncate" style={{ color: F[300] }}>{img.label}</p>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Fallback gradient banner (no photo) */}
      {!hero.image && !hero.images && (
        <div className="px-8 pt-8 pb-6" style={{ background: `linear-gradient(135deg, ${from} 0%, ${to} 100%)` }}>
          <div className="flex items-start justify-between mb-6">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center"
              style={{ background: "rgba(104,150,137,0.25)", border: "1px solid rgba(104,150,137,0.3)" }}>
              <Icon className="w-7 h-7" style={{ color: F[400] }} />
            </div>
            <div className="text-right">
              <p className="text-2xl font-black" style={{ color: F[400], fontFamily: "Chivo, sans-serif" }}>{hero.stat}</p>
              <p className="text-xs" style={{ color: F[300] }}>{hero.statLabel}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {hero.chips.map((chip) => (
              <span key={chip} className="text-xs px-3 py-1 rounded-full font-medium"
                style={{ background: "rgba(255,255,255,0.08)", color: F[300], border: "1px solid rgba(255,255,255,0.1)" }}>
                {chip}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Section renderers ─────────────────────────────────────────────────────────
function RenderSection({ section }) {
  switch (section.type) {
    case "p":
      return (
        <p className="text-base leading-relaxed mb-5" style={{ color: F.m600 }}
          dangerouslySetInnerHTML={{ __html: section.content }} />
      );
    case "h2":
      return (
        <h2 className="text-xl font-black mt-10 mb-4" style={{ fontFamily: "Chivo, sans-serif", color: F[900] }}>
          {section.text}
        </h2>
      );
    case "h3":
      return (
        <h3 className="text-base font-bold mt-6 mb-2" style={{ color: F[800] }}>
          {section.text}
        </h3>
      );
    case "ul":
      return (
        <ul className="mb-5 space-y-2 pl-0">
          {section.items.map((item, i) => (
            <li key={i} className="flex gap-3 text-sm leading-relaxed" style={{ color: F.m600 }}>
              <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full" style={{ background: F[500] }} />
              <span dangerouslySetInnerHTML={{ __html: item }} />
            </li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol className="mb-5 space-y-3 pl-0">
          {section.items.map((item, i) => (
            <li key={i} className="flex gap-3 text-sm leading-relaxed" style={{ color: F.m600 }}>
              <span className="shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold mt-0.5"
                style={{ background: `${F[500]}20`, color: F[500] }}>{i + 1}</span>
              <span dangerouslySetInnerHTML={{ __html: item }} />
            </li>
          ))}
        </ol>
      );
    case "table":
      return (
        <div className="mb-6 overflow-x-auto rounded-xl" style={{ border: `1px solid ${F.c200}` }}>
          <table className="w-full text-sm" style={{ borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: F[900] }}>
                {section.headers.map((h, i) => (
                  <th key={i} className="px-4 py-3 text-left font-semibold text-xs uppercase tracking-wide"
                    style={{ color: F[300], borderBottom: `1px solid ${F[700]}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {section.rows.map((row, ri) => (
                <tr key={ri} style={{ background: ri % 2 === 0 ? F.c50 : F.c100 }}>
                  {row.map((cell, ci) => (
                    <td key={ci} className="px-4 py-3 text-xs leading-relaxed"
                      style={{ color: ci === 0 ? F[800] : F.m600, borderBottom: `1px solid ${F.c200}`, fontWeight: ci === 0 ? 600 : 400 }}
                      dangerouslySetInnerHTML={{ __html: cell }} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "callout":
      return (
        <div className="mb-5 px-5 py-4 rounded-xl text-sm leading-relaxed"
          style={{ background: `${F[500]}12`, border: `1px solid ${F[200]}`, color: F.m600 }}
          dangerouslySetInnerHTML={{ __html: section.content }} />
      );
    case "cta":
      return (
        <div className="my-8 px-6 py-6 rounded-xl text-center" style={{ background: F[900], border: `1px solid ${F[700]}` }}>
          <p className="text-sm mb-4" style={{ color: F[300] }}>{section.text}</p>
          <a href={section.href}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm transition-opacity hover:opacity-90"
            style={{ background: F[500], color: F.c50 }}>
            {section.label} <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      );
    case "image-grid":
      return (
        <div className="my-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
          {section.images.map((img) => (
            <div key={img.src} className="rounded-xl overflow-hidden" style={{ background: F[900], border: `1px solid ${F[700]}` }}>
              <div className="flex items-center justify-center p-4" style={{ height: 110, background: "#1a1a1a" }}>
                <img src={img.src} alt={img.label} className="max-h-full max-w-full object-contain" />
              </div>
              <div className="px-3 py-2">
                <p className="text-xs font-semibold" style={{ color: F[300] }}>{img.label}</p>
                <p className="text-xs" style={{ color: F.m400 }}>{img.sub}</p>
              </div>
            </div>
          ))}
        </div>
      );
    default:
      return null;
  }
}

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = getPostBySlug(slug);

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: F.c100 }}>
        <div className="text-center">
          <p className="text-lg font-semibold mb-4" style={{ color: F[900] }}>Article not found.</p>
          <button onClick={() => navigate("/blog")} className="text-sm underline" style={{ color: F.m600 }}>Back to Blog</button>
        </div>
      </div>
    );
  }

  const canonical = `https://evhubstudio.com/blog/${post.slug}`;
  const relatedPosts = blogPosts.filter((p) => p.slug !== post.slug).slice(0, 2);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.description,
    "datePublished": post.date,
    "dateModified": post.date,
    "author": { "@type": "Organization", "name": "EV Hub Studio" },
    "publisher": { "@type": "Organization", "name": "EV Hub Studio", "url": "https://evhubstudio.com" },
    "url": canonical,
    "mainEntityOfPage": canonical,
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: F.c100 }}>
      <Helmet>
        <title>{post.title} | EV Hub Studio</title>
        <meta name="description" content={post.description} />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hreflang="en-IN" href={canonical} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="EV Hub Studio" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.description} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      {/* Nav */}
      <header className="sticky top-0 z-40 backdrop-blur" style={{ backgroundColor: `${F[800]}f0`, borderBottom: `1px solid ${F[700]}` }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <button onClick={() => navigate("/")} className="flex items-center gap-2.5 transition-opacity hover:opacity-80">
              <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: F[500] }}>
                <Leaf className="w-4 h-4" style={{ color: F.c50 }} />
              </div>
              <span className="font-black text-sm" style={{ fontFamily: "Chivo, sans-serif", color: F.c50 }}>EV Hub Studio</span>
            </button>
            <button onClick={() => navigate("/blog")} className="flex items-center gap-1.5 text-sm transition-colors" style={{ color: F[200] }}
              onMouseEnter={e => e.currentTarget.style.color = F.c50}
              onMouseLeave={e => e.currentTarget.style.color = F[200]}>
              <ArrowLeft className="w-4 h-4" /> Blog
            </button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 max-w-3xl">
        <motion.article initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>

          {/* Header */}
          <header className="mb-10">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: F[50], color: F.m600, border: `1px solid ${F[200]}` }}>
                {post.category}
              </span>
              <span className="flex items-center gap-1 text-xs" style={{ color: F.m400 }}>
                <Clock className="w-3 h-3" />{post.readTime}
              </span>
              <span className="text-xs" style={{ color: F.m400 }}>{post.date}</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-black leading-tight mb-4" style={{ fontFamily: "Chivo, sans-serif", color: F[900] }}>
              {post.title}
            </h1>
            <p className="text-base leading-relaxed" style={{ color: F.m500 }}>{post.description}</p>
          </header>

          {/* Hero banner */}
          {post.hero && <HeroBanner hero={post.hero} category={post.category} />}

          {/* Divider */}
          <div className="mb-8" style={{ borderTop: `2px solid ${F[200]}` }} />

          {/* Body */}
          <div>
            {post.sections.map((section, i) => (
              <RenderSection key={i} section={section} />
            ))}
          </div>

          {/* Tags */}
          {post.tags && (
            <div className="mt-10 pt-6 flex flex-wrap gap-2 items-center" style={{ borderTop: `1px solid ${F.c200}` }}>
              <Tag className="w-3.5 h-3.5" style={{ color: F.m400 }} />
              {post.tags.map((tag) => (
                <span key={tag} className="text-xs px-2.5 py-1 rounded-full"
                  style={{ background: F.c200, color: F.m600 }}>{tag}</span>
              ))}
            </div>
          )}
        </motion.article>

        {/* Related */}
        {relatedPosts.length > 0 && (
          <section className="mt-14">
            <h2 className="text-base font-bold mb-5" style={{ color: F[900] }}>Related Articles</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {relatedPosts.map((p) => (
                <div key={p.slug}
                  className="p-5 rounded-xl cursor-pointer group"
                  style={{ background: F.c50, border: `1px solid ${F.c200}` }}
                  onClick={() => navigate(`/blog/${p.slug}`)}>
                  <p className="text-xs font-semibold mb-1" style={{ color: F.m400 }}>{p.category}</p>
                  <p className="text-sm font-bold leading-snug group-hover:underline" style={{ color: F[900] }}>{p.title}</p>
                  <div className="flex items-center gap-1 mt-3 text-xs" style={{ color: F.m400 }}>
                    <Clock className="w-3 h-3" />{p.readTime}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="mt-16 py-8" style={{ background: F[950], borderTop: `1px solid ${F[800]}` }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm" style={{ color: F.m400 }}>© {new Date().getFullYear()} Stuthi Energy Private Limited</p>
          <div className="flex items-center gap-4 text-sm">
            <a href="/privacy" style={{ color: F.m400 }} onMouseEnter={e => e.currentTarget.style.color = F[300]} onMouseLeave={e => e.currentTarget.style.color = F.m400}>Privacy</a>
            <a href="/terms" style={{ color: F.m400 }} onMouseEnter={e => e.currentTarget.style.color = F[300]} onMouseLeave={e => e.currentTarget.style.color = F.m400}>Terms</a>
            <a href="/contact" style={{ color: F.m400 }} onMouseEnter={e => e.currentTarget.style.color = F[300]} onMouseLeave={e => e.currentTarget.style.color = F.m400}>Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
